<script setup>
import { ref } from 'vue'
import RefPicker from './RefPicker.vue'
import OwnerPicker from './OwnerPicker.vue'
import { useUi } from '@/stores/ui.js'
import { useSession } from '@/stores/session.js'
import { prefillFromText } from '@/lib/quickCreate.js'

/**
 * Participants of a meeting, call or e-mail (D-43): contacts, colleagues, or plain addresses
 * of people outside the CRM. Each is {role, entity?, uuid?, email?, name?}.
 */
const props = defineProps({ modelValue: { type: Array, default: () => [] }, disabled: Boolean })
const emit = defineEmits(['update:modelValue'])
const mode = ref('contact')
const email = ref('')
const name = ref('')
const ui = useUi()
const session = useSession()

/** An outside address becomes a contact (name and e-mail carried over) and joins as one. */
function createContact() {
  const defaults = {
    ...prefillFromText('contact', name.value),
    ...(email.value.includes('@') ? { contact_points: [{ channel: 'email', kind: 'work', value: email.value.trim(), is_primary: true, label: null }] } : {}),
  }
  ui.openCreate({
    entity: 'contact',
    defaults,
    onSaved: (r) => {
      add({ entity: 'contact', uuid: r.uuid, label: r.label })
      email.value = ''
      name.value = ''
    },
  })
}

function add(party) {
  emit('update:modelValue', [...props.modelValue, { role: 'attendee', ...party }])
}
function addExternal() {
  if (!email.value.includes('@')) return
  add({ email: email.value.trim(), name: name.value.trim() || null })
  email.value = ''
  name.value = ''
}
function remove(i) {
  emit('update:modelValue', props.modelValue.filter((_, j) => j !== i))
}
function setRole(i, role) {
  emit('update:modelValue', props.modelValue.map((p, j) => (j === i ? { ...p, role } : p)))
}
const label = (p) => p.label || p.name || p.email || p.party?.label || '…'
</script>

<template>
  <div class="space-y-2">
    <ul v-if="modelValue.length" class="divide-y divide-[var(--border)] rounded-lg border border-default">
      <li v-for="(p, i) in modelValue" :key="i" class="flex items-center gap-2 px-3 py-1.5 text-sm">
        <i :class="['fa-solid fa-fw text-faint', p.entity === 'user' ? 'fa-user-tie' : p.entity ? 'fa-user' : 'fa-at']" aria-hidden="true"></i>
        <span class="min-w-0 flex-1 truncate">{{ label(p) }} <span v-if="p.email && (p.name || p.label)" class="text-faint">· {{ p.email }}</span></span>
        <select class="input w-32 py-1 text-xs" :value="p.role" :disabled="disabled" @change="setRole(i, $event.target.value)">
          <option v-for="r in ['organizer', 'attendee', 'required', 'optional']" :key="r" :value="r">{{ $t(`enums.partyRole.${r}`) }}</option>
        </select>
        <button v-if="!disabled" type="button" class="text-faint hover:text-red-600" :aria-label="$t('common.remove')" @click="remove(i)"><i class="fa-solid fa-xmark" aria-hidden="true"></i></button>
      </li>
    </ul>
    <div v-if="!disabled" class="flex flex-wrap items-start gap-2">
      <select v-model="mode" class="input w-36">
        <option value="contact">{{ $t('entities.contact.one') }}</option>
        <option value="user">{{ $t('entities.user.one') }}</option>
        <option value="external">{{ $t('activity.externalEmail') }}</option>
      </select>
      <RefPicker v-if="mode === 'contact'" class="min-w-48 flex-1" entity="contact" :model-value="null" @select="(r) => r && add({ entity: 'contact', uuid: r.uuid, label: r.label })" />
      <OwnerPicker v-else-if="mode === 'user'" class="min-w-48 flex-1" users-only :model-value="null" @update:model-value="(o) => add({ entity: 'user', uuid: o.uuid, label: o.label })" />
      <template v-else>
        <input v-model="email" type="email" class="input min-w-40 flex-1" placeholder="e-mail" @keydown.enter.prevent="addExternal" />
        <input v-model="name" class="input min-w-32 flex-1" :placeholder="$t('common.name')" @keydown.enter.prevent="addExternal" />
        <button type="button" class="rounded-lg border border-default px-3 py-2 text-sm" @click="addExternal">{{ $t('common.add') }}</button>
        <button v-if="session.has('contact:create')" type="button" class="rounded-lg border border-default px-3 py-2 text-sm" data-testid="party-create-contact" @click="createContact">
          <i class="fa-solid fa-user-plus mr-1" aria-hidden="true"></i>{{ $t('create.asContact') }}
        </button>
      </template>
    </div>
  </div>
</template>
